import type { ReactNode } from 'react';
import { trackHq } from './analytics';
import { HQ_NAME, type HqLinkKind, type HqPain, type HqStage, type HqTool } from './config';
import { HqLink } from './Link';
import { harborUrl } from './urls';

export function HqSmartLink({
  href,
  kind,
  className,
  onClick,
  children,
}: {
  href: string;
  kind: HqLinkKind;
  className?: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  if (kind === 'hq') {
    return (
      <HqLink to={href} className={className} onClick={onClick}>
        {children}
      </HqLink>
    );
  }
  return (
    <a href={href} className={className} onClick={onClick}>
      {children}
    </a>
  );
}

export function trackOutbound(kind: HqLinkKind, extra: Record<string, unknown>): void {
  if (kind === 'harbor') {
    trackHq('HQ Harbor CTA Pressed', extra);
  } else if (kind === 'satellite') {
    trackHq('HQ Tool Clicked', extra);
  }
}

export function HqKicker({ children }: { children: ReactNode }) {
  return (
    <p className='text-xs font-semibold uppercase tracking-[0.16em] text-hq-sage'>{children}</p>
  );
}

export function HqSectionHeading({
  kicker,
  title,
  action,
}: {
  kicker?: string;
  title: string;
  action?: ReactNode;
}) {
  return (
    <div className='flex flex-wrap items-end justify-between gap-3'>
      <div>
        {kicker ? <HqKicker>{kicker}</HqKicker> : null}
        <h2 className='mt-1 font-hq text-3xl font-medium tracking-tight text-hq-ink sm:text-4xl'>
          {title}
        </h2>
      </div>
      {action}
    </div>
  );
}

export function HqStageCard({
  stage,
  index,
}: {
  stage: HqStage;
  index: number;
}) {
  const number = String(index + 1).padStart(2, '0');
  return (
    <article className='flex h-full flex-col rounded-2xl border border-hq-border bg-hq-paper p-5 shadow-[0_1px_0_rgba(28,25,23,0.04)] transition hover:border-hq-sage hover:shadow-[0_10px_24px_rgba(28,25,23,0.06)] sm:p-6'>
      <p className='font-hq text-sm tabular-nums text-hq-sage'>{number}</p>
      <h3 className='mt-2 font-hq text-2xl font-medium tracking-tight text-hq-ink'>{stage.title}</h3>
      <p className='mt-2 text-sm leading-relaxed text-hq-muted'>{stage.blurb}</p>
      <ol className='mt-5 space-y-2 border-t border-hq-border pt-4'>
        {stage.steps.map((step) => (
          <li key={step.label}>
            <HqSmartLink
              href={step.href}
              kind={step.kind}
              className='group flex items-start justify-between gap-3 text-sm font-semibold text-hq-sage hover:text-hq-sage-hover'
              onClick={() => {
                trackHq('HQ Stage Tile Pressed', { stage: stage.id, step: step.label });
                trackOutbound(step.kind, { location: 'stage', stage: stage.id, href: step.href });
              }}
            >
              <span>{step.label}</span>
              <span aria-hidden className='mt-0.5 text-hq-legal transition group-hover:translate-x-0.5'>
                →
              </span>
            </HqSmartLink>
          </li>
        ))}
      </ol>
    </article>
  );
}

export function HqPainChip({ pain }: { pain: HqPain }) {
  return (
    <HqSmartLink
      href={pain.href}
      kind={pain.kind}
      className='inline-flex items-center rounded-full border border-hq-border bg-hq-paper px-3.5 py-2 text-sm text-hq-ink transition hover:border-hq-sage hover:text-hq-sage'
      onClick={() => {
        trackHq('HQ Pain Tile Pressed', { pain: pain.id });
        trackOutbound(pain.kind, { location: 'pain', pain: pain.id, href: pain.href });
      }}
    >
      {pain.title}
    </HqSmartLink>
  );
}

export function HqToolCard({
  tool,
  location,
}: {
  tool: HqTool;
  location: string;
}) {
  return (
    <HqSmartLink
      href={tool.href}
      kind={tool.kind}
      className='block h-full rounded-2xl border border-hq-border bg-hq-paper p-5 transition hover:-translate-y-0.5 hover:border-hq-sage hover:shadow-[0_10px_24px_rgba(28,25,23,0.06)]'
      onClick={() => {
        trackHq('HQ Tool Clicked', { tool: tool.id, location });
        if (tool.kind === 'harbor') {
          trackHq('HQ Harbor CTA Pressed', { location, tool: tool.id });
        }
      }}
    >
      <p className='text-xs font-semibold uppercase tracking-[0.14em] text-hq-sage'>{tool.categoryLabel}</p>
      <h3 className='mt-2 font-hq text-xl font-medium tracking-tight text-hq-ink'>{tool.name}</h3>
      <p className='mt-2 text-sm leading-relaxed text-hq-muted'>{tool.blurb}</p>
      <p className='mt-4 text-xs font-medium text-hq-legal'>Free · no account · {tool.output}</p>
    </HqSmartLink>
  );
}

export function HqGuideCard({
  href,
  title,
  outcome,
  readMinutes,
}: {
  href: string;
  title: string;
  outcome: string;
  readMinutes: number;
}) {
  return (
    <HqLink
      to={href}
      className='block h-full rounded-2xl border border-hq-border bg-hq-paper p-5 transition hover:-translate-y-0.5 hover:border-hq-sage hover:shadow-[0_10px_24px_rgba(28,25,23,0.06)]'
    >
      <h3 className='font-hq text-xl font-medium tracking-tight text-hq-ink'>{title}</h3>
      <p className='mt-2 text-sm leading-relaxed text-hq-muted'>{outcome}</p>
      <p className='mt-4 text-xs font-medium text-hq-legal'>{readMinutes} min read</p>
    </HqLink>
  );
}

export function HqHarborBand({ location }: { location: string }) {
  const items = [
    {
      title: 'Shared calendar',
      body: 'Both parents see the same parenting-time week, not two private spreadsheets.',
    },
    {
      title: 'Expense ledger',
      body: 'Kid costs stay itemized so the reimbursement conversation is about a number, not a fight.',
    },
    {
      title: 'Private journal',
      body: 'A facts-only record you keep for yourself, separate from anything the court named.',
    },
  ];

  return (
    <aside className='rounded-3xl bg-[#E6F4F6] px-6 py-8 sm:px-8 sm:py-10'>
      <p className='text-xs font-semibold uppercase tracking-[0.16em] text-primary'>Harbor</p>
      <h2 className='mt-2 font-hq text-3xl font-medium tracking-tight text-hq-ink sm:text-4xl'>
        When you want this in one shared place
      </h2>
      <p className='mt-3 max-w-2xl text-hq-muted'>
        {HQ_NAME} is the starting place. Harbor is the app if both parents want a calendar,
        ledger, and messages that stay in sync, or if you just want a private file that does not
        walk away with a notebook.
      </p>
      <ul className='mt-6 grid gap-4 sm:grid-cols-3'>
        {items.map((item) => (
          <li key={item.title} className='rounded-2xl bg-white/80 px-4 py-4'>
            <p className='font-semibold text-hq-ink'>{item.title}</p>
            <p className='mt-1 text-sm text-hq-muted'>{item.body}</p>
          </li>
        ))}
      </ul>
      <a
        href={harborUrl('/')}
        className='mt-6 inline-flex items-center rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover'
        onClick={() => trackHq('HQ Harbor CTA Pressed', { location })}
      >
        See Harbor
      </a>
    </aside>
  );
}
